import * as React from "react";

import { Field as FieldPrimitive } from "@base-ui-components/react";
import { cva, VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const atlasLabelFieldVariants = cva(
  "bg-[#2B4F55] text-white relative box-border inline-flex w-full shrink-0 items-center justify-center text-base transition-[color,box-shadow] duration-150 ease-in-out outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "focus-within:outline-none focus-within:ring-0",
        error:
          "border border-red-500 focus-within:border-red-500 focus-within:ring-0",
      },
      size: {
        default: "h-14 rounded-[14px]",
        sm: "h-12 rounded-[14px]",
        lg: "h-16 rounded-[14px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface AtlasLabelFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label: string;
  error?: boolean;
  helperText?: string;
  size?: VariantProps<typeof atlasLabelFieldVariants>["size"];
  variant?: VariantProps<typeof atlasLabelFieldVariants>["variant"];
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
}

const AtlasLabelField = React.forwardRef<
  HTMLInputElement,
  AtlasLabelFieldProps
>(
  (
    {
      className,
      variant,
      size,
      error,
      helperText,
      label,
      startAdornment,
      endAdornment,
      ...props
    },
    ref
  ) => {
    const isDisabled = props.disabled;

    return (
      <div className="w-full">
        <FieldPrimitive.Root
          className={cn(
            atlasLabelFieldVariants({
              variant: error ? "error" : variant,
              size,
            }),
            "group relative items-center gap-0.5",
            isDisabled && "cursor-not-allowed opacity-50",
            startAdornment && "pl-[5px]",
            endAdornment && "pr-[5px]",
            className
          )}
        >
          {startAdornment && (
            <div className="flex h-full items-center justify-center [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
              {startAdornment}
            </div>
          )}
          <div className="relative flex-1">
            <label
              htmlFor={props.id}
              className={cn(
                "absolute left-4 top-2 text-xs font-semibold text-white pointer-events-none",
                error && "text-red-400",
                startAdornment && "left-2"
              )}
            >
              {label}
            </label>
            <FieldPrimitive.Control
              id={props.id}
              className={cn(
                "h-14 w-full flex-1 rounded-[14px] bg-transparent px-4 pt-6 pb-2 outline-none text-white placeholder:text-white/60 file:border-0 file:bg-transparent file:text-base file:font-medium",
                // Override browser autofill styles
                // "autofill:bg-transparent",
                "autofill:[-webkit-text-fill-color:white]",
                "autofill:[transition:background-color_9999s_ease-in-out_0s]",
                isDisabled && "cursor-not-allowed opacity-50",
                startAdornment ? "rounded-l-none pl-2" : "pl-4",
                endAdornment ? "rounded-r-none pr-2" : "pr-4",
                size === "sm" && "h-12 pt-5 pb-1 text-sm",
                size === "lg" && "h-16 pt-7 pb-3 text-lg"
              )}
              {...props}
              ref={ref}
            />
          </div>
          {endAdornment && (
            <div className="flex h-full items-center justify-center [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
              {endAdornment}
            </div>
          )}
        </FieldPrimitive.Root>
        {helperText && (
          <p
            className={cn(
              "mt-1.5 text-sm",
              error ? "text-red-400" : "text-white/70"
            )}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

AtlasLabelField.displayName = "AtlasLabelField";

export { AtlasLabelField, atlasLabelFieldVariants };
